import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  LedgerEntry,
  InvoiceEntry,
  PaymentEntry,
  Patient,
} from '@dataTypes/billing.types'
import {
  fetchLedgerEntries,
  fetchInvoiceEntries,
  fetchPaymentHistoryEntries,
  fetchPatientARAnalytics,
  fetchInsuranceARAnalytics,
  fetchARAging,
  fetchSidePanelAnalytics,
  deleteInvoiceEntry,
  addPaymentEntry,
  removeInvoiceFromSelection,
  updateLedgerNotes,
  updateLedgerAttachments,
  clearSelectedInvoices,
  fetchPatients,
  fetchTransactionEntries,
  setLedgerPagination,
  setInvoicePagination,
  setPaymentPagination,
  setTransactionPagination,
} from '@store/actions/billing.action'
import { TransactionEntry } from '@services/api/billing/transaction.api'
import {
  PatientARAnalytics,
  ARAgingSummaryReport,
  SidePanelAnalytics,
} from '@services/api'

export interface BillingPagination {
  currentPage: number
  pageSize: number
  sort?: string
}

const DEFAULT_PAGE_SIZE = 10

export interface BillingState {
  ledger: LedgerEntry[]
  ledgerCount: number
  ledgerPagination: BillingPagination
  invoices: InvoiceEntry[]
  invoiceCount: number
  invoicePagination: BillingPagination
  selectedInvoices: InvoiceEntry[] // Manually selected invoices from invoice list that we want to pay for
  paymentHistory: PaymentEntry[] // List of payments already made (from backend)
  paymentHistoryCount: number
  paymentPagination: BillingPagination
  transactions: TransactionEntry[]
  transactionCount: number
  transactionPagination: BillingPagination
  patients: Patient[] // All patients in the system
  patientARAnalytics: PatientARAnalytics | null
  insuranceARAnalytics: PatientARAnalytics | null
  arAgingReport: ARAgingSummaryReport | null
  sidePanelAnalytics: SidePanelAnalytics | null
  loadingLedger: boolean
  loadingInvoices: boolean
  loadingPaymentHistory: boolean
  loadingTransactions: boolean
  loadingPatients: boolean
  loadingPatientARAnalytics: boolean
  loadingInsuranceARAnalytics: boolean
  loadingARAging: boolean
  loadingSidePanelAnalytics: boolean
  error: string | null
}

const DEFAULT_LEDGER_SORT = 'appointmentDate:DESC'
const DEFAULT_PAYMENT_HISTORY_SORT = 'date_time_created_at:DESC'
const DEFAULT_INVOICE_SORT = 'invoiceDate:DESC'
const DEFAULT_TRANSACTION_SORT = 'date:DESC'

const initialState: BillingState = {
  ledger: [],
  ledgerCount: 0,
  ledgerPagination: {
    currentPage: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: DEFAULT_LEDGER_SORT,
  },
  invoices: [],
  invoiceCount: 0,
  invoicePagination: {
    currentPage: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: DEFAULT_INVOICE_SORT,
  },
  selectedInvoices: [],
  paymentHistory: [],
  paymentHistoryCount: 0,
  paymentPagination: {
    currentPage: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: DEFAULT_PAYMENT_HISTORY_SORT,
  },
  transactions: [],
  transactionCount: 0,
  transactionPagination: {
    currentPage: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: DEFAULT_TRANSACTION_SORT,
  },
  patients: [],
  patientARAnalytics: null,
  insuranceARAnalytics: null,
  arAgingReport: null,
  sidePanelAnalytics: null,
  loadingLedger: false,
  loadingInvoices: false,
  loadingPaymentHistory: false,
  loadingTransactions: false,
  loadingPatients: false,
  loadingPatientARAnalytics: false,
  loadingInsuranceARAnalytics: false,
  loadingARAging: false,
  loadingSidePanelAnalytics: false,
  error: null,
}

const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(setLedgerPagination, (state, action) => {
        state.ledgerPagination = {
          ...state.ledgerPagination,
          ...action.payload,
        }
      })
      .addCase(setInvoicePagination, (state, action) => {
        state.invoicePagination = {
          ...state.invoicePagination,
          ...action.payload,
        }
      })
      .addCase(setPaymentPagination, (state, action) => {
        state.paymentPagination = {
          ...state.paymentPagination,
          ...action.payload,
        }
      })
      .addCase(setTransactionPagination, (state, action) => {
        state.transactionPagination = {
          ...state.transactionPagination,
          ...action.payload,
        }
      })
      /* -------- Ledger -------- */
      .addCase(fetchLedgerEntries.pending, (state) => {
        state.loadingLedger = true
        state.error = null
      })
      .addCase(
        fetchLedgerEntries.fulfilled,
        (
          state,
          action: PayloadAction<{ entries: LedgerEntry[]; count: number }>,
        ) => {
          state.loadingLedger = false
          state.ledger = action.payload.entries
          state.ledgerCount = action.payload.count
        },
      )
      .addCase(fetchLedgerEntries.rejected, (state, action) => {
        state.loadingLedger = false
        state.error = action.payload as string
      })

      /* -------- Invoices -------- */
      .addCase(fetchInvoiceEntries.pending, (state) => {
        state.loadingInvoices = true
        state.error = null
      })
      .addCase(fetchInvoiceEntries.fulfilled, (state, action) => {
        state.loadingInvoices = false
        state.invoices = action.payload.entries
        state.invoiceCount = action.payload.count
      })
      .addCase(fetchInvoiceEntries.rejected, (state, action) => {
        state.loadingInvoices = false
        state.error = action.payload as string
      })
      .addCase(
        deleteInvoiceEntry.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.invoices = state.invoices.filter(
            (inv) => inv.invoiceNo !== action.payload,
          )
        },
      )
      .addCase(
        addPaymentEntry.fulfilled,
        (state, action: PayloadAction<InvoiceEntry>) => {
          state.selectedInvoices.push(action.payload)
        },
      )
      .addCase(removeInvoiceFromSelection.fulfilled, (state, action) => {
        state.selectedInvoices = state.selectedInvoices.filter(
          (inv) => inv.invoiceNo !== action.payload,
        )
      })
      .addCase(clearSelectedInvoices, (state) => {
        state.selectedInvoices = []
      })

      /* -------- Payment History -------- */
      .addCase(fetchPaymentHistoryEntries.pending, (state) => {
        state.loadingPaymentHistory = true
        state.error = null
      })
      .addCase(
        fetchPaymentHistoryEntries.fulfilled,
        (
          state,
          action: PayloadAction<{ entries: PaymentEntry[]; count: number }>,
        ) => {
          state.loadingPaymentHistory = false
          state.paymentHistory = action.payload.entries
          state.paymentHistoryCount = action.payload.count
        },
      )
      .addCase(fetchPaymentHistoryEntries.rejected, (state, action) => {
        state.loadingPaymentHistory = false
        state.error = action.payload as string
      })
      /* -------- Ledger Memo -------- */
      .addCase(
        updateLedgerNotes.fulfilled,
        (state, action: PayloadAction<LedgerEntry>) => {
          state.ledger = state.ledger.map((entry) =>
            entry.appointmentId === action.payload.appointmentId
              ? { ...entry, notes: action.payload.notes }
              : entry,
          )
        },
      )
      .addCase(
        updateLedgerAttachments.fulfilled,
        (state, action: PayloadAction<LedgerEntry>) => {
          state.ledger = state.ledger.map((entry) =>
            entry.appointmentId === action.payload.appointmentId
              ? { ...entry, attachments: action.payload.attachments }
              : entry,
          )
        },
      )
      .addCase(fetchPatientARAnalytics.pending, (state) => {
        state.loadingPatientARAnalytics = true
        state.error = null
      })
      .addCase(
        fetchPatientARAnalytics.fulfilled,
        (state, action: PayloadAction<PatientARAnalytics>) => {
          state.loadingPatientARAnalytics = false
          state.patientARAnalytics = action.payload
        },
      )
      .addCase(fetchPatientARAnalytics.rejected, (state, action) => {
        state.loadingPatientARAnalytics = false
        state.error = action.payload as string
      })
      .addCase(fetchInsuranceARAnalytics.pending, (state) => {
        state.loadingInsuranceARAnalytics = true
        state.error = null
      })
      .addCase(
        fetchInsuranceARAnalytics.fulfilled,
        (state, action: PayloadAction<PatientARAnalytics>) => {
          state.loadingInsuranceARAnalytics = false
          state.insuranceARAnalytics = action.payload
        },
      )
      .addCase(fetchInsuranceARAnalytics.rejected, (state, action) => {
        state.loadingInsuranceARAnalytics = false
        state.error = action.payload as string
      })
      .addCase(fetchARAging.pending, (state) => {
        state.loadingARAging = true
        state.error = null
      })
      .addCase(
        fetchARAging.fulfilled,
        (state, action: PayloadAction<ARAgingSummaryReport>) => {
          state.loadingARAging = false
          state.arAgingReport = action.payload
        },
      )
      .addCase(fetchARAging.rejected, (state, action) => {
        state.loadingARAging = false
        state.error = action.payload as string
      })
      .addCase(fetchSidePanelAnalytics.pending, (state) => {
        state.loadingSidePanelAnalytics = true
        state.error = null
      })
      .addCase(
        fetchSidePanelAnalytics.fulfilled,
        (state, action: PayloadAction<SidePanelAnalytics>) => {
          state.loadingSidePanelAnalytics = false
          state.sidePanelAnalytics = action.payload
        },
      )
      .addCase(fetchSidePanelAnalytics.rejected, (state, action) => {
        state.loadingSidePanelAnalytics = false
        state.error = action.payload as string
      })

      /* -------- Patients -------- */
      .addCase(fetchPatients.pending, (state) => {
        state.loadingPatients = true
        state.error = null
      })
      .addCase(
        fetchPatients.fulfilled,
        (state, action: PayloadAction<Patient[]>) => {
          state.loadingPatients = false
          state.patients = action.payload
        },
      )
      .addCase(fetchPatients.rejected, (state, action) => {
        state.loadingPatients = false
        state.error = action.payload as string
      })

      /* -------- Transactions -------- */
      .addCase(fetchTransactionEntries.pending, (state) => {
        state.loadingTransactions = true
        state.error = null
      })
      .addCase(
        fetchTransactionEntries.fulfilled,
        (
          state,
          action: PayloadAction<{ entries: TransactionEntry[]; count: number }>,
        ) => {
          state.loadingTransactions = false
          state.transactions = action.payload.entries
          state.transactionCount = action.payload.count
        },
      )
      .addCase(fetchTransactionEntries.rejected, (state, action) => {
        state.loadingTransactions = false
        state.error = action.payload as string
      })
  },
})

export default billingSlice.reducer
